import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, addDoc } from 'firebase/firestore';
import firestore from '@/firebase/firestore';
import { ChevronLeftIcon } from '@heroicons/react/20/solid';
import { AQLConfig, Threshold } from '@/types/AQLConfig';
import AQLForm from './AQLForm';
import { useNotification } from '@/components/Notifications/NotificationProvider';

const DEFAULT_THRESHOLDS: Threshold[] = [
  { lotSize: 8, sampleSize: 2, critical: 0, major: 0, minor: 0 },
  { lotSize: 15, sampleSize: 3, critical: 0, major: 0, minor: 0 },
  { lotSize: 25, sampleSize: 5, critical: 0, major: 0, minor: 0 },
  { lotSize: 50, sampleSize: 8, critical: 0, major: 0, minor: 1 },
  { lotSize: 90, sampleSize: 13, critical: 0, major: 0, minor: 1 },
  { lotSize: 150, sampleSize: 20, critical: 0, major: 0, minor: 1 },
  { lotSize: 280, sampleSize: 32, critical: 0, major: 0, minor: 2 },
  { lotSize: 500, sampleSize: 50, critical: 0, major: 1, minor: 3 },
  { lotSize: 1200, sampleSize: 80, critical: 0, major: 2, minor: 5 },
  { lotSize: 3200, sampleSize: 125, critical: 0, major: 3, minor: 7 },
  { lotSize: 10000, sampleSize: 200, critical: 0, major: 5, minor: 10 },
  { lotSize: 35000, sampleSize: 315, critical: 0, major: 7, minor: 14 },
  { lotSize: 150000, sampleSize: 500, critical: 0, major: 10, minor: 21 },
  { lotSize: 500000, sampleSize: 800, critical: 0, major: 14, minor: 21 },
  { lotSize: Infinity, sampleSize: 1000, critical: 0, major: 14, minor: 21 }
];

const AQLConfigDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [thresholds, setThresholds] = useState<Threshold[]>(DEFAULT_THRESHOLDS);
  const [completeCheck, setCompleteCheck] = useState(false);
  const [loading, setLoading] = useState(true);
  const { showNotification } = useNotification();

  useEffect(() => {
    const loadConfig = async () => {
      if (id && id !== 'add') {
        const docRef = doc(firestore, `aqlConfigs/${id}`);
        const snapshot = await getDoc(docRef);
        if (snapshot.exists()) {
          const data = snapshot.data() as AQLConfig;
          setTitle(data.title);
          setDescription(data.description);
          setThresholds(data.thresholds);
          setCompleteCheck(Boolean(data.completeCheck));
        }
      }
      setLoading(false);
    };
    loadConfig();
  }, [id]);

  const handleSave = async (newThresholds: Threshold[]) => {
    const data: Partial<AQLConfig> = {
      title,
      description,
      thresholds: newThresholds,
      completeCheck,
      updatedAt: new Date()
    };

    if (!title || !thresholds?.length) {
      alert('Please fill in all fields');
      return;
    }

    if (id === 'add') {
      await addDoc(collection(firestore, 'aqlConfigs'), {
        ...data,
        createdAt: new Date()
      });
      showNotification({
        text: 'Configurazione AQL aggiunta con successo',
        type: 'success'
      });
    } else {
      await updateDoc(doc(firestore, `aqlConfigs/${id}`), data);
      showNotification({
        text: 'Configurazione AQL aggiornata con successo',
        type: 'success'
      });
    }

    navigate('/settings/aql-configurations');
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      <div className="mb-4 flex items-center">
        <button
          className="mr-4 flex items-center text-gray-500 hover:text-gray-700"
          onClick={() => navigate(-1)}>
          <ChevronLeftIcon className="mr-2 h-5 w-5" />
          Back
        </button>
        <h1 className="text-xl font-semibold">Configurazione AQL</h1>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700">Titolo</label>
        <input
          className="mt-1 w-full rounded border px-3 py-2"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700">Descrizione</label>
        <textarea
          className="mt-1 w-full rounded border px-3 py-2"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <div className="mb-6 flex items-center">
        <input
          id="complete-check"
          type="checkbox"
          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          checked={completeCheck}
          onChange={(e) => setCompleteCheck(e.target.checked)}
        />
        <label htmlFor="complete-check" className="ml-2 text-sm font-medium text-gray-700">
          Complete check (inspect entire lot)
        </label>
      </div>

      <AQLForm initialThresholds={thresholds} onSave={handleSave} />
    </div>
  );
};

export default AQLConfigDetails;
